import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { socket } from "../App";
import styles from "./Messaging.module.css";
import { t } from "../i18n";
import { useRef } from "react";
import { api } from "../utils/api";
import { getSessionAuth } from "../utils/auth";
import { DEFAULT_AVATAR } from "../utils/avatar";


const Messaging = () => {
  const [userData, setUserData] = useState({});
  const [otherUserData, setOtherUserData] = useState({});
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const { token: userToken, userId } = getSessionAuth();
  const { roomID } = useParams();

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  

  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);
  

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const user = await api.get(`/users/${userId}`);
        setUserData(user);

        const otherUser = await api.get(`/users/${sessionStorage.getItem("toUserID")}`);
        setOtherUserData(otherUser);
      } catch (err) {
        console.error("Failed to fetch users", err);
      }
    };

    fetchUserInfo();
  }, [userId, userToken]);

  useEffect(() => {
    if (!roomID) return;

    const handleMessageReceived = (msg) => {
      const newMsg = {
        ...msg,
        id: msg.id || Date.now(),
        createdAt: msg.createdAt || new Date().toISOString(),
      };
      setMessages((prev) => [...prev, newMsg]);
    };

    socket.on("/send-message", handleMessageReceived);

    const fetchChatHistory = async () => {
      try {
        const result = await api.get(`/chat-history/history/${roomID}`);
        const formatted = result.map((msg) => ({
          id: msg.id,
          fromUserID: msg.fromUserId,
          message: msg.content,
          createdAt: msg.createdAt || new Date().toISOString(),
        }));
        setMessages(formatted);
      } catch (err) {
        console.error("Failed to fetch chat history", err);
      }
    };

    fetchChatHistory();

    return () => {
      socket.off("/send-message", handleMessageReceived);
    };
  }, [roomID, userToken]);


  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleMessageSend = (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) return;

    const timestamp = new Date().toISOString();
    const payload = {
      fromUserID: parseInt(userData.id),
      toUserID: otherUserData.id,
      message: trimmed,
      createdAt: timestamp,
    };

    socket.emit("/chat/send", payload);
    setMessages((prev) => [...prev, { ...payload, id: Date.now() }]);
    setMessage("");
  };

  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  return (
    <div className={styles.chat}>
      <div className={styles.chat__main}>
        <div className={styles.otherUserHeader}>
          <img
            src={otherUserData?.attributes?.picture || DEFAULT_AVATAR}
            alt="Profile"
            className={styles.profilePic}
          />
          <p>{t("messaging:")} {otherUserData?.attributes?.username || otherUserData?.email}</p>
        </div>

        <div className={styles.message__container}>
          {sortedMessages.map((msg) => (
            <div className={styles.message__chats} key={msg.id}>
              {msg.fromUserID === userData.id ? (
                <>
                  <div className={styles.sender__info}>
                    <img
                      src={userData?.attributes?.picture || DEFAULT_AVATAR}
                      alt="Sender's Profile"
                      className={styles.profilePic}
                    />
                    <p className={styles.sender__name}>
                      <strong>{userData?.attributes?.username || userData?.email}</strong>
                      <span className={styles.message__timestamp}> {formatTimestamp(msg.createdAt)}</span>
                    </p>
                  </div>
                  <p className={styles.message__sender}>{msg.message}</p>
                </>
              ) : (
                <>
                  <div className={styles.recipient__info}>
                    <img
                      src={otherUserData?.attributes?.picture || DEFAULT_AVATAR}
                      alt="Recipient's Profile"
                      className={styles.profilePic}
                    />
                    <p className={styles.recipient__name}>
                      <strong>{otherUserData?.attributes?.username || otherUserData?.email}</strong>
                      <span className={styles.message__timestamp}> {formatTimestamp(msg.createdAt)}</span>
                    </p>
                  </div>
                  <p className={styles.message__recipient}>{msg.message}</p>
                </>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className={styles.chat__footer}>
          <form className={styles.form} onSubmit={handleMessageSend}>
            <input
              type="text"
              placeholder={t("write_message")}
              className="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <button className={styles.sendBtn} type="submit">
              {t("send")}

            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Messaging;
